/**
 * ONE seat-pricing vocabulary for every surface that shows or charges a seat:
 * storefront (select-seats), box office, and the section APIs behind both.
 *
 * Before this module the two surfaces resolved a seat's price by different
 * rules — the storefront walked `categoryMapping` by id with add-ons filtered
 * out, while the section APIs matched the seat CATEGORY NAME against the
 * TICKET TYPE NAME and fell back to `ticketTypes[0]` (unordered, add-ons
 * included). A category named "VIP" against a type named "VIP Table" missed
 * and priced every seat at whatever row Postgres happened to return first,
 * which is how box office and storefront ended up showing different money for
 * the same seat.
 *
 * The rules, in authority order, are the storefront's — it was correct:
 *   1. explicit `categoryMapping` (by id, add-ons refused as a price source)
 *   2. 4-level category-name -> ticket-type-name fallback, add-ons refused
 *   3. first non-add-on ticket type, deterministically ordered
 */

import {
  type CategoryMappingEntry,
  resolveCategoryPriceSources,
  sortTicketTypesDeterministic,
} from '@/lib/ticket-type-order'

export interface PricingTicketType {
  id: string
  name: string
  price: number
  allInPrice?: number | null
  isAddon?: boolean
  channelRestrict?: string | null
  sortOrder?: number | null
}

/**
 * Penny-exact per-table split.
 *
 * MUST stay identical to the split in getSeatCartData (bulletproof-seat-inventory)
 * — that is the charge path, this is every display path. Plain rounding charged
 * 6 x $108.33 = $649.98 for a $650 table; the first `remainder` seats (in the
 * same deterministic order the server uses) each carry one extra cent so the
 * table sums to EXACTLY the configured price.
 */
export function perTableShare(total: number, divisor: number, idx: number): number {
  if (!(divisor > 1) || !(total > 0)) return total
  const totalCents = Math.round(total * 100)
  const base = Math.floor(totalCents / divisor)
  const remainder = totalCents - base * divisor
  return (base + (idx < remainder ? 1 : 0)) / 100
}

/**
 * True when the section's admin-configured price covers the WHOLE table/booth
 * rather than one seat. Only TABLE/BOOTH sections with an explicit
 * `pricingMode: 'per-table'` divide — everything else is already per-seat.
 */
export function isPerTableSection(section: {
  sectionType?: string | null
  tableConfig?: { pricingMode?: string | null } | null
} | null | undefined): boolean {
  if (!section) return false
  const t = section.sectionType
  if (t !== 'TABLE' && t !== 'BOOTH') return false
  return section.tableConfig?.pricingMode === 'per-table'
}

/**
 * A TABLE/BOOTH/GA section that sells as ONE unit rather than seat by seat.
 * TABLE defaults to whole-table unless explicitly set to 'by-seat' — the same
 * rule UnifiedSeatMap uses to route a click to onSectionClick, and the same one
 * SectionSeatCache.shouldLoadSeats uses to decide whether to draw seats at all.
 */
export function isUnitSection(section: {
  sectionType?: string | null
  bookingMode?: string | null
  tableConfig?: { bookingMode?: string | null } | null
} | null | undefined): boolean {
  if (!section) return false
  const t = section.sectionType
  if (t === 'GA' || t === 'BOOTH') return true
  const mode = section.tableConfig?.bookingMode ?? section.bookingMode ?? null
  if (t === 'TABLE') return mode !== 'by-seat'
  return mode === 'whole-table' || mode === 'variable'
}

/**
 * Resolve categoryId -> ticket type for pricing/labelling.
 *
 * `ticketTypes` may be passed in any order; it is sorted deterministically
 * (sortOrder asc, id asc) internally so legacy index-based mapping entries and
 * the `[0]` fallback resolve to the SAME type on every call and every surface.
 */
export function buildCategoryTicketTypeResolver<T extends PricingTicketType>(
  ticketTypes: T[],
  categoryMapping: CategoryMappingEntry[] | null | undefined,
  categories?: Array<{ id: string; name: string }> | null
): (categoryId?: string | null, categoryName?: string | null) => T | null {
  const ordered = sortTicketTypesDeterministic(ticketTypes)
  const seatable = ordered.filter(t => !t.isAddon)
  const mapped = resolveCategoryPriceSources(categoryMapping || [], ordered)
  const categoryNameById = new Map((categories || []).map(c => [c.id, c.name]))

  return (categoryId, categoryName) => {
    if (categoryId) {
      const m = mapped.get(categoryId)
      if (m) return m
    }
    const name = categoryName || (categoryId ? categoryNameById.get(categoryId) : null)
    if (name) {
      const lower = name.toLowerCase()
      return (
        seatable.find(t => t.name === name) ||
        seatable.find(t => t.name.toLowerCase() === lower) ||
        seatable.find(
          t => t.name.toLowerCase().includes(lower) || lower.includes(t.name.toLowerCase())
        ) ||
        seatable[0] ||
        null
      )
    }
    return seatable[0] || null
  }
}

/**
 * The per-seat base price a surface should DISPLAY for a seat.
 *
 * Mirrors the charge path exactly: the baked EventSeatStatus price wins over
 * the live ticket-type price (an admin repricing a type post-publish must not
 * make the map disagree with what the cart charges), and a per-table section's
 * price is divided the same penny-exact way getSeatCartData divides it.
 *
 * `bakedPrice` MUST be passed as null — not 0 — when no EventSeatStatus row
 * exists. A genuine $0 comp seat is a real price and is preserved here.
 */
export function resolveSeatBasePrice(opts: {
  bakedPrice: number | null | undefined
  ticketType?: PricingTicketType | null
  perTable?: boolean
  /** Real active seat count for the section — the server's divisor. */
  tableSeatCount?: number | null
  /** Deterministic index of this seat within the section. */
  seatIndexInSection?: number
}): number | null {
  const { bakedPrice, ticketType, perTable, tableSeatCount, seatIndexInSection = 0 } = opts
  const raw = bakedPrice != null ? Number(bakedPrice) : (ticketType?.price ?? null)
  if (raw == null) return null
  if (perTable && tableSeatCount && tableSeatCount > 1) {
    return perTableShare(raw, tableSeatCount, seatIndexInSection)
  }
  return raw
}

/**
 * The server's per-table seat order: row number, then seat index, then id —
 * the order getSeatCartDataMany walks a section when it hands out the odd
 * cents of a split. Computed over EVERY active seat of the section, before
 * any channel filter: a seat only the box office can sell is still part of
 * the table, so it still counts and still shifts its neighbours' indexes.
 */
export function perTableSeatOrder(
  seats: Array<{ id: string; rowNumber: number; seatIndex: number }>
): { seatCount: number; indexById: Map<string, number> } {
  const ordered = seats
    .slice()
    .sort((a, b) => a.rowNumber - b.rowNumber || a.seatIndex - b.seatIndex || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  return { seatCount: ordered.length, indexById: new Map(ordered.map((s, i) => [s.id, i])) }
}

export interface PerTableIndex {
  /** Divisor: every active seat in the section, whatever the channel. */
  count: number
  /** Each seat's position in the server's order — decides who carries the odd cents. */
  indexById: Map<string, number>
}

/**
 * Client side of perTableSeatOrder: read the count and index the section
 * endpoints report (`seatCount`, `pricingIndex`). A response without them
 * (the season endpoints) falls back to the order and length of the seats it
 * contains, which is right whenever every seat is visible to this channel.
 */
export function buildPerTableIndex(detail: {
  seatCount?: number | null
  rows?: Array<{ seats?: Array<{ seatId: string; pricingIndex?: number | null }> }> | null
}): PerTableIndex {
  const seats = (detail.rows || []).flatMap(r => r.seats || [])
  const stamped = seats.every(s => typeof s.pricingIndex === 'number')
  const indexById = new Map<string, number>(seats.map((s, i) => [s.seatId, stamped ? (s.pricingIndex as number) : i]))
  return { count: detail.seatCount ?? seats.length, indexById }
}
